/**
 * The whole planner as ops — trip co-editing Stage D
 * (docs/plans/TRIP_COEDITING_PLAN.md §5). Stage C made the stops ops
 * (src/utils/tripActivityOps.js); this adds the days, stays, transports and
 * trip dates, so a planner in ops mode sends no whole-itinerary save at all.
 *
 * Same shape as the stops: `planItineraryOps(prev, next)` compares the
 * planner's itinerary before and after an edit and returns the ops that say
 * what changed (and `next` with the order keys its stop ops chose);
 * `replayItinerary(itin, entries)` re-applies pending ops onto a fresh server
 * copy. An itinerary is { days, unscheduled, transports, destinations }.
 *
 * Destinations (D11, batch 3) are the Trip setup place chips: one op per
 * chip, named by its place (the part of its name before the first comma —
 * the server's identity for a destination). A leg's chip (`source:
 * 'derived'`) is the server's, written from the days; no op names one.
 *
 * Unscheduled (D5) — stops taken off a removed day — is handed to the stop
 * planner as one more day (UNSCHEDULED_UID, last), so a stop moving on or off
 * it is an ordinary stop move; on the wire its day is `null`.
 *
 * Op order within one edit matters and is fixed here: trip dates first (spans
 * clamp to the trip's last day); removed days, then re-dates, then new days —
 * each frees the dates the next takes (removing day 1 slides the rest back
 * onto its date; inserting a day pushes the rest on before it takes theirs);
 * then day fields, stop ops (a removed day's stops are already Unscheduled
 * or gone on the server — the day removal says which — so theirs are no-ops
 * there), stays, transports and destinations.
 */
import { planActivityOps, applyEntry as applyStopEntry, mintOpId, stopKey } from './tripActivityOps';
import { rescheduleActivityToDate } from './activityUtilities';

export const UNSCHEDULED_UID = '__unscheduled__';

// A day's fields an op carries: op field ← the planner day's keys.
const DAY_FIELDS = {
  location: ['location'],
  locations: ['locations'],
  latitude: ['latitude'],
  longitude: ['longitude'],
  notes: ['notes'],
  day_label: ['dayLabel', 'day_label'],
  variant_label: ['variant_label'],
  variant_icon: ['variant_icon'],
  is_active_variant: ['is_active_variant'],
  waived_plans: ['waivedPlans', 'waived_plans'],
  travel_estimates: ['travelEstimates', 'travel_estimates']
};
// One set op per group, so a refused field never takes the others with it.
const DAY_GROUPS = [
  ['location', 'locations', 'latitude', 'longitude'],
  ['notes', 'day_label'],
  ['variant_label', 'variant_icon', 'is_active_variant'],
  ['waived_plans'],
  ['travel_estimates']
];

const pickKey = (o, keys) => {
  for (const k of keys) if (o && o[k] !== undefined) return o[k];
  return undefined;
};
const dayField = (day, field) => {
  const v = pickKey(day, DAY_FIELDS[field]);
  if (field === 'is_active_variant') return v === undefined || v === null ? 1 : (v ? 1 : 0);
  if (field === 'notes' || field === 'day_label' || field === 'variant_label' || field === 'variant_icon' || field === 'location') {
    return typeof v === 'string' && v.trim() ? v : null;
  }
  if (field === 'waived_plans') return Array.isArray(v) ? v : [];
  if (field === 'travel_estimates') return v && typeof v === 'object' ? v : {};
  return v === undefined ? null : v;
};
const sameJson = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
const dayPatch = (fields, next) => {
  const patch = {};
  fields.forEach(f => DAY_FIELDS[f].forEach(k => { if (k in next) patch[k] = next[k]; }));
  return patch;
};
const dayFieldsOf = (day) => {
  const out = {};
  Object.keys(DAY_FIELDS).forEach(f => {
    const v = dayField(day, f);
    if (v !== null && !(Array.isArray(v) && v.length === 0) && !(typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0)) out[f] = v;
  });
  out.is_active_variant = dayField(day, 'is_active_variant');
  return out;
};

/** A day field as the server answered it (overwrote.their_value), as the planner's keys. */
export function dayPatchFromWire(field, value) {
  const keys = DAY_FIELDS[field] || [field];
  return { [keys[0]]: value ?? null };
}

// ── stays ───────────────────────────────────────────────────────────────────

// The fields of a stay the save stores (tripsService.saveAccommodationsFromTripDays).
const STAY_KEYS = ['name', 'address', 'type', 'pricePerNight', 'cost', 'checkInTime', 'checkOutTime', 'placeId',
  'nameAddressVisibility', 'bookingReference', 'notes', 'bookingUrl', 'coordinates', 'homeUserId'];
const stayBody = (acc) => {
  const out = {};
  STAY_KEYS.forEach(k => { if (acc[k] !== undefined && acc[k] !== null) out[k] = acc[k]; });
  return out;
};

/**
 * The stays the days carry, by client uid: the stay and the uids of the days
 * it covers — on one plan only (the GET hands a stay to every plan of a date,
 * so a Plan B day shows the same stay; its nights are the main plan's).
 */
function staysOf(days) {
  const byUid = new Map();
  (days || []).forEach(day => {
    const acc = day && day.accommodation;
    if (!acc || !acc.clientUid || !acc.name) return;
    const list = byUid.get(acc.clientUid) || [];
    list.push(day);
    byUid.set(acc.clientUid, list);
  });
  const out = new Map();
  byUid.forEach((list, uid) => {
    const variants = [...new Set(list.map(d => d.plan_variant || 'A'))];
    const variant = variants.includes('A') ? 'A' : variants.sort()[0];
    const own = list.filter(d => (d.plan_variant || 'A') === variant);
    // The nights' dates too: a re-dated day moves the stay's span (the
    // server stores a stay as first..last night), so the stay is re-said.
    out.set(uid, { stay: own[0].accommodation, dayUids: own.map(d => d.uid).sort(), dates: own.map(d => d.date).sort() });
  });
  return out;
}

// ── transports ──────────────────────────────────────────────────────────────

// Read back by the GET from the airport / station tables, never stored.
const TRANSPORT_DERIVED = ['id', 'createdBy', 'originCoordinates', 'destinationCoordinates', 'originCity', 'destinationCity'];
const transportBody = (t) => {
  const out = { ...t };
  TRANSPORT_DERIVED.forEach(k => { delete out[k]; });
  delete out.clientUid;
  return out;
};

// ── destinations ────────────────────────────────────────────────────────────

/** A destination's place: the part of its name before the first comma, lower case. */
export const destinationKey = (d) => String((d && (d.name || d.destination_name)) || '').trim().toLowerCase().split(',')[0].trim();
const isChip = (d) => !!d && d.source !== 'derived' && !!destinationKey(d);
const chipsByKey = (list) => {
  const out = new Map();
  (list || []).forEach(d => { if (isChip(d) && !out.has(destinationKey(d))) out.set(destinationKey(d), d); });
  return out;
};
const chipFields = (d) => {
  const out = { name: String(d.name || d.destination_name).trim() };
  const type = d.type || d.destination_type;
  if (type) out.type = type;
  const placeId = d.placeId || d.place_id;
  if (placeId) out.place_id = placeId;
  if (d.coordinates && d.coordinates.lat != null && d.coordinates.lng != null) out.coordinates = { lat: d.coordinates.lat, lng: d.coordinates.lng };
  return out;
};
const hasPin = (d) => !!(d && d.coordinates && d.coordinates.lat != null);
const placeIdOf = (d) => (d && (d.placeId || d.place_id)) || null;

// ── plan ────────────────────────────────────────────────────────────────────

const withVirtual = (days, unscheduled) => [...(days || []), { uid: UNSCHEDULED_UID, date: null, activities: unscheduled || [] }];

/**
 * The ops that turn itinerary `prev` into `next`.
 *
 * @param {{days, unscheduled, transports}} prev
 * @param {{days, unscheduled, transports}} next - every day with a uid
 * @param {object} [opts]
 * @param {'unschedule'|'remove'} [opts.dayRemoval] - what a removed day's
 *        stops do on the server (the planner has already moved or dropped
 *        them in `next`; this answers for any it couldn't see)
 * @param {{start_date?, end_date?}} [opts.dates] - trip dates this edit set
 * @param {{start_date?, end_date?}} [opts.seenDates] - the dates it replaced
 * @returns {{ itinerary, entries }}
 */
export function planItineraryOps(prev, next, { dayRemoval = 'unschedule', dates = null, seenDates = null, mintOpId: mint = mintOpId } = {}) {
  const entries = [];
  const prevDays = prev.days || [];
  const nextDays = next.days || [];
  const before = new Map(prevDays.map(d => [d.uid, d]));
  const after = new Map(nextDays.map(d => [d.uid, d]));

  // Trip dates.
  if (dates && Object.keys(dates).length) {
    const fields = {};
    const seen = {};
    Object.keys(dates).forEach(k => {
      if ((seenDates || {})[k] !== dates[k]) { fields[k] = dates[k] || null; seen[k] = (seenDates || {})[k] || null; }
    });
    if (Object.keys(fields).length) {
      entries.push({ op: { op_id: mint(), type: 'trip.set', target: 'trip', fields, seen }, replay: { kind: 'trip', fields } });
    }
  }

  // Removed days.
  prevDays.forEach(d => {
    if (after.has(d.uid)) return;
    entries.push({ op: { op_id: mint(), type: 'day.remove', target: d.uid, fields: { stops: dayRemoval } }, replay: { kind: 'day.remove', uid: d.uid, stops: dayRemoval } });
  });

  // Days on other dates.
  // A plan moved to another date may take another letter there.
  const redates = {};
  const plans = {};
  nextDays.forEach(d => {
    const was = before.get(d.uid);
    if (!was || was.date === d.date) return;
    redates[d.uid] = d.date;
    if ((was.plan_variant || 'A') !== (d.plan_variant || 'A')) plans[d.uid] = d.plan_variant || 'A';
  });
  if (Object.keys(redates).length) {
    const fields = Object.keys(plans).length ? { dates: redates, plans } : { dates: redates };
    entries.push({ op: { op_id: mint(), type: 'days.redate', target: 'trip', fields }, replay: { kind: 'days.redate', dates: redates, plans } });
  }

  // New days.
  nextDays.forEach(d => {
    if (before.has(d.uid)) return;
    entries.push({
      op: { op_id: mint(), type: 'day.add', target: d.uid, fields: { date: d.date, plan_variant: d.plan_variant || 'A', ...dayFieldsOf(d) } },
      replay: { kind: 'day.add', day: { ...d, activities: [] } }
    });
  });

  // Day fields.
  nextDays.forEach(d => {
    const was = before.get(d.uid);
    if (!was) return;
    DAY_GROUPS.forEach(group => {
      const fields = {};
      const seen = {};
      group.forEach(f => {
        if (sameJson(dayField(was, f), dayField(d, f))) return;
        fields[f] = dayField(d, f);
        seen[f] = dayField(was, f);
      });
      if (!Object.keys(fields).length) return;
      entries.push({ op: { op_id: mint(), type: 'day.set', target: d.uid, fields, seen }, replay: { kind: 'day.set', uid: d.uid, patch: dayPatch(Object.keys(fields), d) } });
    });
  });

  // Stops (Unscheduled as one more day).
  const stops = planActivityOps(withVirtual(prevDays, prev.unscheduled), withVirtual(nextDays, next.unscheduled), { mintOpId: mint });
  stops.entries.forEach(e => {
    if (e.op.fields && e.op.fields.day_uid === UNSCHEDULED_UID) {
      e.op.fields.day_uid = null;
      delete e.op.fields.time_block;
    }
    entries.push(e);
  });
  const virtual = stops.days[stops.days.length - 1];
  const keyedDays = stops.days.slice(0, -1);

  // Stays.
  const staysBefore = staysOf(prevDays);
  const staysAfter = staysOf(nextDays);
  staysAfter.forEach(({ stay, dayUids }, uid) => {
    const was = staysBefore.get(uid);
    if (was && sameJson(was.dayUids, dayUids) && sameJson(was.dates, staysAfter.get(uid).dates) && sameJson(stayBody(was.stay), stayBody(stay))) return;
    entries.push({
      op: { op_id: mint(), type: 'stay.set', target: uid, fields: { day_uids: dayUids, stay: stayBody(stay) } },
      replay: { kind: 'stay.set', uid, dayUids, stay }
    });
  });
  staysBefore.forEach((_, uid) => {
    if (staysAfter.has(uid)) return;
    entries.push({ op: { op_id: mint(), type: 'stay.remove', target: uid }, replay: { kind: 'stay.remove', uid } });
  });

  // Transports.
  const tBefore = new Map((prev.transports || []).filter(t => t && t.clientUid).map(t => [t.clientUid, t]));
  const tAfter = new Map((next.transports || []).filter(t => t && t.clientUid).map(t => [t.clientUid, t]));
  tAfter.forEach((t, uid) => {
    const was = tBefore.get(uid);
    if (was && sameJson(transportBody(was), transportBody(t))) return;
    entries.push({ op: { op_id: mint(), type: 'transport.set', target: uid, fields: { transport: transportBody(t) } }, replay: { kind: 'transport.set', transport: t } });
  });
  tBefore.forEach((_, uid) => {
    if (tAfter.has(uid)) return;
    entries.push({ op: { op_id: mint(), type: 'transport.remove', target: uid }, replay: { kind: 'transport.remove', uid } });
  });

  // Destinations: a chip added (or, already there, given the pin or place
  // it lacked — the server fills, never overwrites), a chip removed.
  if (Array.isArray(prev.destinations) && Array.isArray(next.destinations) && prev.destinations !== next.destinations) {
    const dBefore = chipsByKey(prev.destinations);
    const dAfter = chipsByKey(next.destinations);
    dAfter.forEach((d, key) => {
      const was = dBefore.get(key);
      if (was && !(hasPin(d) && !hasPin(was)) && !(placeIdOf(d) && !placeIdOf(was))) return;
      const fields = chipFields(d);
      entries.push({ op: { op_id: mint(), type: 'destination.add', target: fields.name, fields }, replay: { kind: 'destination.add', key, chip: d } });
    });
    dBefore.forEach((d, key) => {
      if (dAfter.has(key)) return;
      entries.push({ op: { op_id: mint(), type: 'destination.remove', target: chipFields(d).name }, replay: { kind: 'destination.remove', key } });
    });
  }

  // The days and Unscheduled with the keys the stop ops chose — the same
  // objects where nothing was re-keyed, so a no-op edit changes nothing.
  const daysKeyed = keyedDays.some((d, i) => d !== nextDays[i]);
  const unscheduledKeyed = virtual.activities !== (next.unscheduled || []);
  if (!daysKeyed && !unscheduledKeyed) return { itinerary: next, entries };
  return {
    itinerary: {
      ...next,
      days: daysKeyed ? keyedDays : next.days,
      unscheduled: unscheduledKeyed ? virtual.activities : next.unscheduled
    },
    entries
  };
}

// ── replay ──────────────────────────────────────────────────────────────────

const byDate = (a, b) => {
  if (a.date !== b.date) return a.date < b.date ? -1 : 1;
  const va = a.plan_variant || 'A';
  const vb = b.plan_variant || 'A';
  return va < vb ? -1 : va > vb ? 1 : 0;
};

/** One entry replayed onto an itinerary; the input back when it changes nothing. */
export function applyItineraryEntry(itin, entry) {
  const r = entry.replay || {};
  const days = itin.days || [];
  switch (r.kind) {
    case 'add': case 'set': case 'move': case 'remove': {
      const all = withVirtual(days, itin.unscheduled);
      const out = applyStopEntry(all, entry);
      if (out === all) return itin;
      const virtual = out[out.length - 1];
      return {
        ...itin,
        days: out.slice(0, -1),
        unscheduled: virtual.activities.map(a => (a.scheduledDate ? { ...a, scheduledDate: null, endDate: null } : a))
      };
    }
    case 'day.add': {
      if (days.some(d => d.uid === r.day.uid)) return itin;
      return { ...itin, days: [...days, r.day].sort(byDate) };
    }
    case 'days.redate': {
      let changed = false;
      const out = days.map(d => {
        const date = r.dates[d.uid];
        if (!date || date === d.date) return d;
        changed = true;
        const plan = (r.plans || {})[d.uid];
        return { ...d, date, ...(plan ? { plan_variant: plan } : {}), activities: (d.activities || []).map(a => rescheduleActivityToDate(a, date)) };
      });
      return changed ? { ...itin, days: out.sort(byDate) } : itin;
    }
    case 'day.set': {
      const i = days.findIndex(d => d.uid === r.uid);
      if (i === -1) return itin;
      const out = days.slice();
      out[i] = { ...days[i], ...r.patch };
      return { ...itin, days: out };
    }
    case 'day.remove': {
      const gone = days.find(d => d.uid === r.uid);
      if (!gone) return itin;
      const moved = r.stops === 'remove' ? [] : (gone.activities || []).map(a => ({ ...a, scheduledDate: null, endDate: null }));
      return { ...itin, days: days.filter(d => d.uid !== r.uid), unscheduled: [...(itin.unscheduled || []), ...moved] };
    }
    case 'stay.set': {
      // On every plan of the nights it covers, as the GET hands it out.
      const nights = new Set(days.filter(d => r.dayUids.includes(d.uid)).map(d => d.date));
      let changed = false;
      const out = days.map(d => {
        if (nights.has(d.date)) { changed = true; return { ...d, accommodation: r.stay }; }
        if (d.accommodation && d.accommodation.clientUid === r.uid) {
          changed = true;
          return { ...d, accommodation: null };
        }
        return d;
      });
      return changed ? { ...itin, days: out } : itin;
    }
    case 'stay.remove': {
      if (!days.some(d => d.accommodation && d.accommodation.clientUid === r.uid)) return itin;
      return { ...itin, days: days.map(d => (d.accommodation && d.accommodation.clientUid === r.uid ? { ...d, accommodation: null } : d)) };
    }
    case 'transport.set': {
      const list = itin.transports || [];
      const i = list.findIndex(t => t.clientUid === r.transport.clientUid);
      const out = list.slice();
      if (i === -1) out.push(r.transport); else out[i] = { ...list[i], ...r.transport };
      return { ...itin, transports: out };
    }
    case 'transport.remove': {
      const list = itin.transports || [];
      if (!list.some(t => t.clientUid === r.uid)) return itin;
      return { ...itin, transports: list.filter(t => t.clientUid !== r.uid) };
    }
    case 'destination.add': {
      const list = itin.destinations || [];
      const at = list.findIndex(d => destinationKey(d) === r.key);
      // A leg covers it (the server keeps the leg's chip), or it is there:
      // only what it lacked is filled in.
      if (at !== -1) {
        const d = list[at];
        if (d.source === 'derived') return itin;
        const fill = {};
        if (!hasPin(d) && hasPin(r.chip)) fill.coordinates = r.chip.coordinates;
        if (!placeIdOf(d) && placeIdOf(r.chip)) fill.placeId = placeIdOf(r.chip);
        if (!Object.keys(fill).length) return itin;
        const out = list.slice();
        out[at] = { ...d, ...fill };
        return { ...itin, destinations: out };
      }
      return { ...itin, destinations: [...list, r.chip] };
    }
    case 'destination.remove': {
      const list = itin.destinations || [];
      if (!list.some(d => isChip(d) && destinationKey(d) === r.key)) return itin;
      return { ...itin, destinations: list.filter(d => !(isChip(d) && destinationKey(d) === r.key)) };
    }
    default:
      return itin; // trip settings live in the planner's form state
  }
}

/** A server itinerary with these entries replayed on top, in order. */
export function replayItinerary(itin, entries) {
  let out = itin;
  (entries || []).forEach(e => { out = applyItineraryEntry(out, e); });
  return out;
}

/** Day uids an op names (to know whether the server can have them yet). */
export function dayUidsNamed(op) {
  const f = op.fields || {};
  if (op.type === 'day.add') return [];
  if (op.type.startsWith('day.')) return [op.target];
  if (op.type === 'days.redate') return Object.keys(f.dates || {});
  if (op.type === 'stay.set') return f.day_uids || [];
  if (f.day_uid) return [f.day_uid];
  return [];
}

export { stopKey };
